import { BridgeError } from '@/lib/bridge';

/**
 * 把异常翻成「用户能看懂的一句话」。
 *
 * 页面上直接把 `error.message` / 原生错误码贴出去会变成黑话
 * （`risk_verification`、`bridge timeout: data.overview`、`DioException …`），
 * 所有上屏的错误都必须过这里。原始细节保留在 console / 诊断日志里。
 */

const FALLBACK = '出了点问题，请稍后重试。';

/** 原生桥错误码 → 中文说明。 */
const CODE_TEXT: Record<string, string> = {
  unknown_method: '当前 App 版本不支持该功能，请升级后重试。',
  invalid_params: '这次操作没成功，请退出后重进再试。',
  account_not_found: '没有找到这个账号，请重新添加后再试。',
  login_required: '请先在 App 内登录这个账号。',
  account_banned: '这个账号当前不可用。',
  rate_limited: '操作太频繁了，稍等几秒再试。',
  risk_verification: '需要先在抖音完成安全验证，然后重试。',
  insufficient_credits: '额度不足，请稍后再试。',
  cancelled: '已取消。',
  network_error: '网络连接失败，请检查网络后重试。',
  unsupported: '这个平台暂不支持该功能，敬请期待。',
  unknown: FALLBACK,
};

/** 是否是一段可以直接上屏的「人话」。 */
function looksUserFacing(text: string): boolean {
  if (text.length === 0 || text.length > 80) return false;
  const cjk = (text.match(/[一-龥]/g) ?? []).length;
  if (cjk < 2) return false;
  // 中文里夹着异常类名、状态码、下划线代码的，仍然算技术串
  return !/_[a-z]+|[a-z]Exception|status ?code|http \d{3}|stack trace|package:/i.test(
    text,
  );
}

/** 任意错误 → 可上屏文案。 */
export function describeError(error: unknown): string {
  if (error instanceof BridgeError) {
    const code = String(error.code);
    const byCode = CODE_TEXT[code] ?? FALLBACK;
    const raw = (error.message ?? '').trim();
    // 原生没给文案时 message 就是 code 本身，直接查表
    if (!raw || raw === code) return byCode;
    return looksUserFacing(raw) ? raw : byCode;
  }

  const raw = (error instanceof Error ? error.message : String(error ?? '')).trim();
  return looksUserFacing(raw) ? raw : FALLBACK;
}
